import React, { useState } from "react";
import { format } from "date-fns";
import { FiChevronDown } from "react-icons/fi";
import { formatCurrency } from "../../utils/formatCurrency";
import MeasurementCompare from "./MeasurementCompare";

const formatWhen = (value) => {
  if (!value) return "";
  try {
    return format(new Date(value), "dd MMM yyyy, HH:mm");
  } catch {
    return String(value);
  }
};

/**
 * Admin accordion panel: when a parcel was remesured/amended and why.
 * @param {{ amendments?: object[], symbol?: string, defaultOpen?: boolean }} props
 */
const AmendmentDetailsPanel = ({
  amendments = [],
  symbol = "GBP",
  defaultOpen = false,
}) => {
  const rows = Array.isArray(amendments) ? amendments : [];
  const [open, setOpen] = useState(defaultOpen);
  if (!rows.length) return null;

  const latest = rows[rows.length - 1];

  return (
    <div className="rounded-xl border border-amber-200 bg-amber-50/70 min-w-0 overflow-hidden">
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setOpen((v) => !v);
        }}
        aria-expanded={open}
        className="w-full flex items-center justify-between gap-2 px-3 py-2.5 text-left hover:bg-amber-50/80"
      >
        <div className="flex flex-wrap items-center gap-2 min-w-0">
          <p className="text-[11px] font-bold uppercase tracking-wide text-amber-800">
            Amendment details
          </p>
          <span className="inline-flex px-1.5 py-0.5 rounded text-[10px] font-semibold border border-amber-200 bg-white text-amber-900">
            {rows.length} version{rows.length === 1 ? "" : "s"}
          </span>
          {latest?.adjustmentType ? (
            <span className="inline-flex px-1.5 py-0.5 rounded text-[10px] font-semibold border border-amber-200 bg-amber-50 text-amber-900">
              Latest: {latest.adjustmentType}
              {latest.version != null ? ` · V${latest.version}` : ""}
            </span>
          ) : null}
        </div>
        <FiChevronDown
          className={`shrink-0 text-amber-800 transition-transform ${
            open ? "rotate-180" : ""
          }`}
          aria-hidden
        />
      </button>
      {open ? (
      <div className="px-3 pb-3">
      <ul className="space-y-3">
        {rows.map((row, idx) => (
          <li
            key={`${row.paymentInvoiceNo || row.adjustmentType || "amend"}-${idx}`}
            className="rounded-lg border border-amber-100 bg-white px-3 py-2.5"
          >
            <div className="flex flex-wrap items-center gap-2 mb-2">
              {row.adjustmentType ? (
                <span className="inline-flex px-1.5 py-0.5 rounded text-[10px] font-semibold border border-amber-200 bg-amber-50 text-amber-900">
                  {row.adjustmentType}
                  {row.version != null ? ` · V${row.version}` : ""}
                </span>
              ) : null}
              {row.isCurrent ? (
                <span className="inline-flex px-1.5 py-0.5 rounded text-[10px] font-semibold border border-emerald-200 bg-emerald-50 text-emerald-800">
                  Current
                </span>
              ) : null}
              {row.orderAmount != null && row.orderAmount !== "" ? (
                <span className="text-xs font-semibold text-stone-800 tabular-nums">
                  {formatCurrency(row.orderAmount, symbol)}
                </span>
              ) : null}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-[6.5rem_minmax(0,1fr)] gap-0.5 sm:gap-2 text-sm">
              <span className="text-xs font-semibold uppercase tracking-wide text-stone-500">
                When
              </span>
              <span className="text-stone-900">
                {row.when ? formatWhen(row.when) : "—"}
              </span>
              <span className="text-xs font-semibold uppercase tracking-wide text-stone-500">
                Why
              </span>
              <span className="text-stone-900 break-words">
                {row.why || "No reason recorded"}
              </span>
              {row.paymentInvoiceNo ? (
                <>
                  <span className="text-xs font-semibold uppercase tracking-wide text-stone-500">
                    Invoice
                  </span>
                  <span className="font-mono text-xs text-stone-700 break-all">
                    {row.paymentInvoiceNo}
                  </span>
                </>
              ) : null}
              {row.parentInvoiceNo ? (
                <>
                  <span className="text-xs font-semibold uppercase tracking-wide text-stone-500">
                    From
                  </span>
                  <span className="font-mono text-xs text-stone-700 break-all">
                    {row.parentInvoiceNo}
                  </span>
                </>
              ) : null}
            </div>
            {row.customerInput ||
            row.hubMeasurement ||
            row.changeSummary ? (
              <div className="mt-2 pt-2 border-t border-amber-100">
                <MeasurementCompare
                  customerInput={row.customerInput}
                  hubMeasurement={row.hubMeasurement}
                  weightBandName={row.weightBandName}
                  dimensionName={row.dimensionName}
                  previousMeasurement={row.previousMeasurement}
                  changeSummary={row.changeSummary}
                />
              </div>
            ) : null}
          </li>
        ))}
      </ul>
      </div>
      ) : null}
    </div>
  );
};

export default AmendmentDetailsPanel;
